import { Portfolio, RiskLevel } from '@fruitful/types';
import { useQueryClient } from '@tanstack/react-query';
import { type Href, Link } from 'expo-router';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { Tag } from 'primereact/tag';
import React, { useCallback, useMemo, useState } from 'react';
import {
    BodyMediumRegular,
    BodySmallEmphasized,
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    CheckboxWithLabel,
    H6Emphasized,
    Input,
    Spinner,
    useToastController,
    XStack,
    YStack,
} from 'smoothie';

import {
    MutationErrorToastOptions,
    MutationSuccessToastOptions,
} from '../../common/components/Toasts';
import { APEX_REBALANCER_URL } from '../../common/utils/LinkUtils';
import {
    REBALANCER_ACCOUNTS_QUERY_KEY,
    useChangeRebalancerModel,
    useGetBackdoorRothPreflight,
    useGetRebalancerAccounts,
    useImportApexAccount,
    usePairBackdoorRoth,
} from '../hooks/useRebalancerData';
import type {
    ImportApexAccountResponse,
    ImportPlannedAction,
    RebalancerAccountRow,
} from '../types/RebalancerTypes';
import { RebalancerAccountFilter } from '../types/RebalancerTypes';

// ---------------------------------------------------------------------------
// Filter options
// ---------------------------------------------------------------------------

const FILTER_OPTIONS = [
    { label: 'All Open Accounts', value: RebalancerAccountFilter.ALL_OPEN },
    {
        label: 'Backdoor Roth Candidates',
        value: RebalancerAccountFilter.BACKDOOR_ROTH_CANDIDATES,
    },
    {
        label: 'Missing Rebalancer Model',
        value: RebalancerAccountFilter.MISSING_REBALANCER_MODEL,
    },
];

const PORTFOLIO_OPTIONS = Object.values(Portfolio).map((value) => ({
    label: value,
    value,
}));

const RISK_LEVEL_OPTIONS = Object.values(RiskLevel).map((value) => ({
    label: value,
    value,
}));

// ---------------------------------------------------------------------------
// Main Panel
// ---------------------------------------------------------------------------

export const ImOpsRebalancerPanel = () => {
    const [filter, setFilter] = useState<RebalancerAccountFilter>(
        RebalancerAccountFilter.ALL_OPEN,
    );
    const [accountSearch, setAccountSearch] = useState('');
    const [changeModelAccount, setChangeModelAccount] =
        useState<RebalancerAccountRow | null>(null);
    const [pairAccount, setPairAccount] = useState<RebalancerAccountRow | null>(
        null,
    );
    const [showImportModal, setShowImportModal] = useState(false);
    const [allRows, setAllRows] = useState<RebalancerAccountRow[]>([]);
    const handleRowsLoaded = useCallback((rows: RebalancerAccountRow[]) => {
        setAllRows(rows);
    }, []);

    return (
        <YStack gap={32} width="100%">
            <RebalancerAccountsTable
                filter={filter}
                onFilterChange={setFilter}
                accountSearch={accountSearch}
                onAccountSearchChange={setAccountSearch}
                onChangeModel={setChangeModelAccount}
                onPairBackdoorRoth={setPairAccount}
                onRowsLoaded={handleRowsLoaded}
                onImportAccount={() => {
                    setShowImportModal(true);
                }}
            />
            <ChangeModelModal
                account={changeModelAccount}
                onClose={() => {
                    setChangeModelAccount(null);
                }}
            />
            <PairBackdoorRothModal
                account={pairAccount}
                allRows={allRows}
                onClose={() => {
                    setPairAccount(null);
                }}
            />
            <ImportAccountModal
                visible={showImportModal}
                onClose={() => {
                    setShowImportModal(false);
                }}
            />
        </YStack>
    );
};

// ---------------------------------------------------------------------------
// Accounts Table
// ---------------------------------------------------------------------------

interface RebalancerAccountsTableProps {
    filter: RebalancerAccountFilter;
    onFilterChange: (f: RebalancerAccountFilter) => void;
    accountSearch: string;
    onAccountSearchChange: (s: string) => void;
    onChangeModel: (account: RebalancerAccountRow) => void;
    onPairBackdoorRoth: (account: RebalancerAccountRow) => void;
    onRowsLoaded: (rows: RebalancerAccountRow[]) => void;
    onImportAccount: () => void;
}

const IRA_REGISTRATION_TYPES = [
    'REGISTRATION_IRA_ROTH',
    'REGISTRATION_IRA_TRADITIONAL',
];

const RebalancerAccountsTable = ({
    filter,
    onFilterChange,
    accountSearch,
    onAccountSearchChange,
    onChangeModel,
    onPairBackdoorRoth,
    onRowsLoaded,
    onImportAccount,
}: RebalancerAccountsTableProps) => {
    const { data, isLoading, refetch, isFetching } = useGetRebalancerAccounts({
        filter,
    });

    const flatRows = useMemo(() => {
        if (data == null) return [];
        const rows: RebalancerAccountRow[] = [];
        for (const membership of data) {
            for (const member of membership.members) {
                for (const account of member.accounts) {
                    rows.push(account);
                }
            }
        }
        return rows;
    }, [data]);

    React.useEffect(() => {
        onRowsLoaded(flatRows);
    }, [flatRows, onRowsLoaded]);

    const filteredRows = useMemo(() => {
        if (accountSearch.trim() === '') return flatRows;
        const search = accountSearch.trim().toLowerCase();
        return flatRows.filter(
            (row) =>
                row.accountNumber.toLowerCase().includes(search) ||
                row.apexAccountId.toLowerCase().includes(search),
        );
    }, [flatRows, accountSearch]);

    if (isLoading) {
        return <Spinner />;
    }

    return (
        <YStack gap={12}>
            <XStack alignItems="center" gap={12}>
                <H6Emphasized>Rebalancer Accounts</H6Emphasized>
                <XStack flex={1} />
                <Button
                    text="Import UTMA Account"
                    variant={ButtonVariant.SECONDARY}
                    size={ButtonSize.TINY}
                    onPress={onImportAccount}
                    width="auto"
                />
                <a
                    href={APEX_REBALANCER_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ fontSize: 13 }}
                >
                    Apex Rebalancer
                </a>
            </XStack>
            <XStack gap={12} alignItems="center">
                <Dropdown
                    value={filter}
                    options={FILTER_OPTIONS}
                    onChange={(e) => {
                        onFilterChange(e.value as RebalancerAccountFilter);
                    }}
                    placeholder="Select filter"
                    style={{ width: 240 }}
                />
                <Input
                    placeholder="Search by account number..."
                    value={accountSearch}
                    onChangeText={onAccountSearchChange}
                    size="tiny"
                    width={220}
                />
                <BodySmallRegular color="$gray700" whiteSpace="nowrap">
                    {filteredRows.length} account
                    {filteredRows.length !== 1 ? 's' : ''}
                </BodySmallRegular>
                <XStack flex={1} />
                <Button
                    text={isFetching ? 'Refreshing...' : 'Refresh'}
                    variant={ButtonVariant.SECONDARY}
                    size={ButtonSize.TINY}
                    disabled={isFetching}
                    onPress={() => {
                        void refetch();
                    }}
                    width="auto"
                />
            </XStack>
            <DataTable
                value={filteredRows}
                size="small"
                tableStyle={{ width: '100%', fontSize: 13 }}
                scrollable
                paginator
                rows={25}
                rowsPerPageOptions={[25, 50, 100]}
                dataKey="apexAccountId"
                emptyMessage="No accounts found."
            >
                <Column
                    field="accountNumber"
                    header="Account #"
                    sortable
                    style={{ minWidth: 120 }}
                />
                <Column
                    field="registrationType"
                    header="Type"
                    sortable
                    style={{ minWidth: 100 }}
                />
                <Column
                    field="state"
                    header="State"
                    sortable
                    body={(row: RebalancerAccountRow) => (
                        <Tag
                            value={row.state}
                            severity={
                                row.state === 'OPEN' ? 'success' : 'warning'
                            }
                        />
                    )}
                    style={{ minWidth: 80 }}
                />
                <Column
                    field="apexPortfolio"
                    header="Portfolio"
                    sortable
                    body={(row: RebalancerAccountRow) =>
                        row.apexPortfolio ?? '—'
                    }
                    style={{ minWidth: 90 }}
                />
                <Column
                    field="apexRiskLevel"
                    header="Risk"
                    sortable
                    body={(row: RebalancerAccountRow) =>
                        row.apexRiskLevel ?? '—'
                    }
                    style={{ minWidth: 60 }}
                />
                <Column
                    header=""
                    body={(row: RebalancerAccountRow) => {
                        const disabled = row.backdoorRothRole === 'TRADITIONAL';
                        return (
                            <Button
                                text="Change"
                                variant={ButtonVariant.TERTIARY}
                                size={ButtonSize.TINY}
                                disabled={disabled}
                                onPress={() => {
                                    onChangeModel(row);
                                }}
                            />
                        );
                    }}
                    style={{ minWidth: 80 }}
                />
                <Column
                    field="backdoorRothRole"
                    header="BD Roth Role"
                    sortable
                    body={(row: RebalancerAccountRow) =>
                        row.backdoorRothRole ?? '—'
                    }
                    style={{ minWidth: 110 }}
                />
                <Column
                    header="Paired With"
                    body={(row: RebalancerAccountRow) => {
                        if (row.backdoorRothPairedApexAccountId != null) {
                            const paired = flatRows.find(
                                (r) =>
                                    r.apexAccountId ===
                                    row.backdoorRothPairedApexAccountId,
                            );
                            return (
                                <BodySmallRegular>
                                    {paired?.accountNumber ??
                                        row.backdoorRothPairedApexAccountId}
                                </BodySmallRegular>
                            );
                        }
                        const isIra = IRA_REGISTRATION_TYPES.includes(
                            row.registrationType,
                        );
                        if (!isIra || row.backdoorRothRole != null) return null;
                        return (
                            <Button
                                text="Pair"
                                variant={ButtonVariant.TERTIARY}
                                size={ButtonSize.TINY}
                                onPress={() => {
                                    onPairBackdoorRoth(row);
                                }}
                            />
                        );
                    }}
                    style={{ minWidth: 100 }}
                />
                <Column
                    field="nickname"
                    header="Nickname"
                    body={(row: RebalancerAccountRow) => row.nickname ?? '—'}
                    style={{ minWidth: 100 }}
                />
                <Column
                    header="Member"
                    body={(row: RebalancerAccountRow) => {
                        const name = [row.userFirstName, row.userLastName]
                            .filter(Boolean)
                            .join(' ');
                        return (
                            <Link
                                href={`/membership/${row.membershipId}` as Href}
                            >
                                {name || row.userEmail}
                            </Link>
                        );
                    }}
                    style={{ minWidth: 150 }}
                />
            </DataTable>
        </YStack>
    );
};

// ---------------------------------------------------------------------------
// Change Model Modal
// ---------------------------------------------------------------------------

interface ChangeModelModalProps {
    account: RebalancerAccountRow | null;
    onClose: () => void;
}

const toEnumValue = <T extends string>(
    val: string | null,
    enumObj: Record<string, T>,
): T | null => {
    if (val == null) return null;
    return Object.values(enumObj).find((v) => v === val) ?? null;
};

const ChangeModelModal = ({ account, onClose }: ChangeModelModalProps) => {
    const toast = useToastController();
    const queryClient = useQueryClient();
    const [portfolio, setPortfolio] = useState<Portfolio | null>(null);
    const [riskLevel, setRiskLevel] = useState<RiskLevel | null>(null);
    const [clearModel, setClearModel] = useState(false);

    const currentPortfolio = toEnumValue(
        account?.apexPortfolio ?? null,
        Portfolio,
    );
    const currentRiskLevel = toEnumValue(
        account?.apexRiskLevel ?? null,
        RiskLevel,
    );

    const effectivePortfolio = portfolio ?? currentPortfolio;
    const effectiveRiskLevel = riskLevel ?? currentRiskLevel;

    const hasModelToClear =
        account != null &&
        (account.apexPortfolio != null ||
            account.apexRiskLevel != null ||
            account.apexRebalancerGoalId != null);

    const resetForm = () => {
        setPortfolio(null);
        setRiskLevel(null);
        setClearModel(false);
    };

    const handleClose = () => {
        onClose();
        resetForm();
    };

    const { mutateAsync, isPending } = useChangeRebalancerModel({
        onSuccess: (_data, variables) => {
            const removed =
                variables.body?.portfolio == null &&
                variables.body?.riskLevel == null;
            toast.show(
                removed
                    ? 'Rebalancer model removed'
                    : 'Rebalancer model updated',
                MutationSuccessToastOptions,
            );
            handleClose();
            void queryClient.invalidateQueries({
                predicate: (query) =>
                    typeof query.queryKey[0] === 'string' &&
                    query.queryKey[0].startsWith(REBALANCER_ACCOUNTS_QUERY_KEY),
            });
        },
        onError: (error) => {
            toast.show(
                'Failed to change model',
                MutationErrorToastOptions(
                    error instanceof Error ? error.message : String(error),
                ),
            );
        },
    });

    const canSubmit = clearModel
        ? hasModelToClear && !isPending
        : effectivePortfolio != null &&
          effectiveRiskLevel != null &&
          !isPending;

    return (
        <Dialog
            visible={account != null}
            onHide={handleClose}
            header="Change Rebalancer Model"
            style={{ width: 420 }}
            modal
        >
            {account != null && (
                <YStack gap={16} padding={4}>
                    <YStack gap={4}>
                        <BodySmallRegular color="$gray700">
                            Account
                        </BodySmallRegular>
                        <BodySmallEmphasized>
                            {account.accountNumber} ({account.registrationType})
                        </BodySmallEmphasized>
                    </YStack>
                    <XStack gap={8}>
                        <BodySmallRegular color="$gray700">
                            Current:{' '}
                        </BodySmallRegular>
                        <BodySmallEmphasized>
                            {account.apexPortfolio ?? '—'} / Risk{' '}
                            {account.apexRiskLevel ?? '—'}
                        </BodySmallEmphasized>
                    </XStack>
                    <YStack gap={4}>
                        <BodySmallEmphasized>Portfolio</BodySmallEmphasized>
                        <Dropdown
                            value={effectivePortfolio}
                            options={PORTFOLIO_OPTIONS}
                            onChange={(e) => {
                                setPortfolio(e.value as Portfolio);
                            }}
                            placeholder="Select portfolio"
                            style={{ width: '100%' }}
                            disabled={clearModel}
                        />
                    </YStack>
                    <YStack gap={4}>
                        <BodySmallEmphasized>Risk Level</BodySmallEmphasized>
                        <Dropdown
                            value={effectiveRiskLevel}
                            options={RISK_LEVEL_OPTIONS}
                            onChange={(e) => {
                                setRiskLevel(e.value as RiskLevel);
                            }}
                            placeholder="Select risk level"
                            style={{ width: '100%' }}
                            disabled={clearModel}
                        />
                    </YStack>
                    <CheckboxWithLabel
                        id="clear-rebalancer-model"
                        label="Clear rebalancer model"
                        checked={clearModel}
                        disabled={!hasModelToClear}
                        onPress={() => {
                            setClearModel((prev) => !prev);
                        }}
                    />
                    <XStack gap={12} justifyContent="flex-end" marginTop={8}>
                        <Button
                            text="Cancel"
                            variant={ButtonVariant.SECONDARY}
                            size={ButtonSize.SMALL}
                            onPress={handleClose}
                        />
                        <Button
                            text={clearModel ? 'Remove Model' : 'Change Model'}
                            variant={ButtonVariant.PRIMARY}
                            size={ButtonSize.SMALL}
                            disabled={!canSubmit}
                            onPress={() => {
                                if (clearModel) {
                                    void mutateAsync({
                                        body: {
                                            apexAccountId:
                                                account.apexAccountId,
                                            portfolio: null,
                                            riskLevel: null,
                                        },
                                    });
                                    return;
                                }
                                if (
                                    effectivePortfolio == null ||
                                    effectiveRiskLevel == null
                                )
                                    return;
                                void mutateAsync({
                                    body: {
                                        apexAccountId: account.apexAccountId,
                                        portfolio: effectivePortfolio,
                                        riskLevel: effectiveRiskLevel,
                                    },
                                });
                            }}
                        />
                    </XStack>
                </YStack>
            )}
        </Dialog>
    );
};

// ---------------------------------------------------------------------------
// Pair Backdoor Roth Modal
// ---------------------------------------------------------------------------

interface PairBackdoorRothModalProps {
    account: RebalancerAccountRow | null;
    allRows: RebalancerAccountRow[];
    onClose: () => void;
}

const PairBackdoorRothModal = ({
    account,
    allRows,
    onClose,
}: PairBackdoorRothModalProps) => {
    const toast = useToastController();
    const queryClient = useQueryClient();
    const [selectedPartnerAccountId, setSelectedPartnerAccountId] = useState<
        string | null
    >(null);
    const [showPreflight, setShowPreflight] = useState(false);

    const isTrad = account?.registrationType === 'REGISTRATION_IRA_TRADITIONAL';
    const partnerType = isTrad
        ? 'REGISTRATION_IRA_ROTH'
        : 'REGISTRATION_IRA_TRADITIONAL';

    const eligiblePartners = useMemo(() => {
        if (account == null) return [];
        return allRows.filter(
            (row) =>
                row.userId === account.userId &&
                row.registrationType === partnerType &&
                row.backdoorRothRole == null &&
                row.apexAccountId !== account.apexAccountId,
        );
    }, [account, allRows, partnerType]);

    const partnerOptions = useMemo(
        () =>
            eligiblePartners.map((row) => ({
                label: `${row.accountNumber} (${row.nickname ?? row.registrationType})`,
                value: row.apexAccountId,
            })),
        [eligiblePartners],
    );

    const selectedPartner = eligiblePartners.find(
        (r) => r.apexAccountId === selectedPartnerAccountId,
    );

    const tradApexAccountId = isTrad
        ? (account?.apexAccountId ?? '')
        : (selectedPartner?.apexAccountId ?? '');
    const rothApexAccountId = isTrad
        ? (selectedPartner?.apexAccountId ?? '')
        : (account?.apexAccountId ?? '');

    const canPreflight = tradApexAccountId !== '' && rothApexAccountId !== '';

    const {
        data: preflightData,
        isLoading: isPreflightLoading,
        refetch: refetchPreflight,
    } = useGetBackdoorRothPreflight(
        { tradApexAccountId, rothApexAccountId },
        { enabled: showPreflight && canPreflight },
    );

    const { mutateAsync: pairMutation, isPending: isPairing } =
        usePairBackdoorRoth({
            onSuccess: () => {
                toast.show(
                    'Backdoor Roth pairing created',
                    MutationSuccessToastOptions,
                );
                handleClose();
                void queryClient.invalidateQueries({
                    predicate: (query) =>
                        typeof query.queryKey[0] === 'string' &&
                        query.queryKey[0].startsWith(
                            REBALANCER_ACCOUNTS_QUERY_KEY,
                        ),
                });
            },
            onError: (error) => {
                toast.show(
                    'Failed to pair',
                    MutationErrorToastOptions(
                        error instanceof Error ? error.message : String(error),
                    ),
                );
            },
        });

    const handleClose = () => {
        onClose();
        setSelectedPartnerAccountId(null);
        setShowPreflight(false);
    };

    return (
        <Dialog
            visible={account != null}
            onHide={handleClose}
            header="Pair Backdoor Roth IRA"
            style={{ width: 520 }}
            modal
        >
            {account != null && (
                <YStack gap={16} padding={4}>
                    <YStack gap={4}>
                        <BodySmallRegular color="$gray700">
                            Selected Account ({isTrad ? 'Traditional' : 'Roth'}{' '}
                            IRA)
                        </BodySmallRegular>
                        <BodySmallEmphasized>
                            {account.accountNumber} —{' '}
                            {account.nickname ?? account.registrationType}
                        </BodySmallEmphasized>
                    </YStack>

                    <YStack gap={4}>
                        <BodySmallEmphasized>
                            Partner {isTrad ? 'Roth' : 'Traditional'} IRA
                        </BodySmallEmphasized>
                        {partnerOptions.length === 0 ? (
                            <BodySmallRegular color="$red1100">
                                No eligible {isTrad ? 'Roth' : 'Traditional'}{' '}
                                IRA accounts found for this user.
                            </BodySmallRegular>
                        ) : (
                            <Dropdown
                                value={selectedPartnerAccountId}
                                options={partnerOptions}
                                onChange={(e) => {
                                    setSelectedPartnerAccountId(
                                        e.value as string,
                                    );
                                    setShowPreflight(false);
                                }}
                                placeholder="Select paired account"
                                style={{ width: '100%' }}
                            />
                        )}
                    </YStack>

                    {selectedPartnerAccountId != null && (
                        <Button
                            text="Run Preflight"
                            variant={ButtonVariant.SECONDARY}
                            size={ButtonSize.SMALL}
                            disabled={!canPreflight}
                            onPress={() => {
                                setShowPreflight(true);
                                void refetchPreflight();
                            }}
                        />
                    )}

                    {showPreflight && isPreflightLoading && <Spinner />}

                    {showPreflight && preflightData != null && (
                        <YStack gap={12}>
                            <YStack
                                gap={4}
                                padding={12}
                                backgroundColor="$gray100"
                                borderRadius={6}
                            >
                                <BodySmallEmphasized>
                                    Preflight Results
                                </BodySmallEmphasized>
                                <BodySmallRegular>
                                    Trad IRA:{' '}
                                    {preflightData.tradIra.accountNumber} (
                                    {preflightData.tradIra.registrationType}) —
                                    Balance: $
                                    {(
                                        preflightData.tradIra
                                            .accountEquityCents / 100
                                    ).toFixed(2)}
                                </BodySmallRegular>
                                {preflightData.tradIra.contributionSummaries.map(
                                    (row) => (
                                        <BodySmallRegular
                                            key={row.taxYear}
                                            paddingLeft={12}
                                        >
                                            {row.taxYear ?? '—'}:{' '}
                                            {row.regularAmount ?? '0'} regular,{' '}
                                            {row.rolloverAmount ?? '0'} rollover
                                        </BodySmallRegular>
                                    ),
                                )}
                                <BodySmallRegular>
                                    Roth IRA:{' '}
                                    {preflightData.rothIra.accountNumber} (
                                    {preflightData.rothIra.registrationType}) —
                                    Balance: $
                                    {(
                                        preflightData.rothIra
                                            .accountEquityCents / 100
                                    ).toFixed(2)}
                                </BodySmallRegular>
                                {preflightData.rothIra.contributionSummaries.map(
                                    (row) => (
                                        <BodySmallRegular
                                            key={row.taxYear}
                                            paddingLeft={12}
                                        >
                                            {row.taxYear ?? '—'}:{' '}
                                            {row.regularAmount ?? '0'} regular,{' '}
                                            {row.rolloverAmount ?? '0'} rollover
                                        </BodySmallRegular>
                                    ),
                                )}
                                {preflightData.otherIraAccounts.length > 0 && (
                                    <YStack gap={4} marginTop={8}>
                                        <BodySmallEmphasized>
                                            Other IRA Accounts (pro-rata
                                            relevant)
                                        </BodySmallEmphasized>
                                        {preflightData.otherIraAccounts.map(
                                            (acct) => (
                                                <YStack
                                                    key={acct.apexAccountId}
                                                    gap={2}
                                                >
                                                    <BodySmallRegular>
                                                        {acct.accountNumber} (
                                                        {acct.registrationType})
                                                        — Balance: $
                                                        {(
                                                            acct.accountEquityCents /
                                                            100
                                                        ).toFixed(2)}
                                                    </BodySmallRegular>
                                                    {acct.contributionSummaries.map(
                                                        (row) => (
                                                            <BodySmallRegular
                                                                key={
                                                                    row.taxYear
                                                                }
                                                                paddingLeft={12}
                                                            >
                                                                {row.taxYear ??
                                                                    '—'}
                                                                :{' '}
                                                                {row.regularAmount ??
                                                                    '0'}{' '}
                                                                regular,{' '}
                                                                {row.rolloverAmount ??
                                                                    '0'}{' '}
                                                                rollover
                                                            </BodySmallRegular>
                                                        ),
                                                    )}
                                                </YStack>
                                            ),
                                        )}
                                    </YStack>
                                )}
                                {preflightData.proRataWarning !== '' && (
                                    <BodyMediumRegular color="$orange1000">
                                        ⚠️ {preflightData.proRataWarning}
                                    </BodyMediumRegular>
                                )}
                            </YStack>

                            <XStack
                                gap={12}
                                justifyContent="flex-end"
                                marginTop={8}
                            >
                                <Button
                                    text="Cancel"
                                    variant={ButtonVariant.SECONDARY}
                                    size={ButtonSize.SMALL}
                                    onPress={handleClose}
                                />
                                <Button
                                    text="Pair Backdoor Roth"
                                    variant={ButtonVariant.PRIMARY}
                                    size={ButtonSize.SMALL}
                                    disabled={isPairing}
                                    onPress={() => {
                                        void pairMutation({
                                            body: {
                                                tradApexAccountId,
                                                rothApexAccountId,
                                            },
                                        });
                                    }}
                                />
                            </XStack>
                        </YStack>
                    )}
                </YStack>
            )}
        </Dialog>
    );
};

// ---------------------------------------------------------------------------
// Import UTMA Account Modal
// ---------------------------------------------------------------------------

interface ImportAccountModalProps {
    visible: boolean;
    onClose: () => void;
}

const ValidationBadge = ({
    label,
    passed,
}: {
    label: string;
    passed: boolean;
}) => (
    <Tag
        value={`${label}: ${passed ? 'Match' : 'Mismatch'}`}
        severity={passed ? 'success' : 'warning'}
    />
);

const PlannedActionRow = ({ action }: { action: ImportPlannedAction }) => (
    <YStack gap={2} padding={8} backgroundColor="$gray100" borderRadius={4}>
        <XStack gap={8} alignItems="center">
            <Tag
                value={action.type}
                severity={action.type === 'API_CALL' ? 'info' : undefined}
                style={{ fontSize: 11 }}
            />
            <BodySmallEmphasized>{action.description}</BodySmallEmphasized>
        </XStack>
        {action.table != null && (
            <BodySmallRegular color="$gray700">
                Table: {action.table}
            </BodySmallRegular>
        )}
        {action.service != null && (
            <BodySmallRegular color="$gray700">
                {action.service}.{action.method}
            </BodySmallRegular>
        )}
    </YStack>
);

const ImportAccountModal = ({ visible, onClose }: ImportAccountModalProps) => {
    const toast = useToastController();
    const queryClient = useQueryClient();
    const [apexAccountId, setApexAccountId] = useState('');
    const [portfolio, setPortfolio] = useState<Portfolio | null>(null);
    const [riskLevel, setRiskLevel] = useState<RiskLevel | null>(null);
    const [dryRunResult, setDryRunResult] =
        useState<ImportApexAccountResponse | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const { mutateAsync, isPending } = useImportApexAccount({
        onError: (error) => {
            setErrorMessage(
                error instanceof Error ? error.message : String(error),
            );
        },
    });

    const resetForm = () => {
        setApexAccountId('');
        setPortfolio(null);
        setRiskLevel(null);
        setDryRunResult(null);
        setErrorMessage(null);
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    const canSubmit =
        apexAccountId.trim() !== '' &&
        portfolio != null &&
        riskLevel != null &&
        !isPending;

    const handleDryRun = async () => {
        if (portfolio == null || riskLevel == null) return;
        setErrorMessage(null);
        try {
            const result = await mutateAsync({
                body: {
                    apexAccountId: apexAccountId.trim(),
                    portfolio,
                    riskLevel,
                    dryRun: true,
                },
            });
            setDryRunResult(result);
        } catch {
            // error handled by onError callback
        }
    };

    const handleExecute = async () => {
        if (portfolio == null || riskLevel == null) return;
        setErrorMessage(null);
        try {
            const result = await mutateAsync({
                body: {
                    apexAccountId: apexAccountId.trim(),
                    portfolio,
                    riskLevel,
                    dryRun: false,
                },
            });
            setDryRunResult(result);
            toast.show('UTMA account imported', MutationSuccessToastOptions);
            void queryClient.invalidateQueries({
                predicate: (query) =>
                    typeof query.queryKey[0] === 'string' &&
                    query.queryKey[0].startsWith(REBALANCER_ACCOUNTS_QUERY_KEY),
            });
        } catch {
            // error handled by onError callback
        }
    };

    const isExecuted = dryRunResult != null && !dryRunResult.dryRun;

    return (
        <Dialog
            visible={visible}
            onHide={handleClose}
            header="Import UTMA Account"
            style={{ width: 560 }}
            modal
        >
            <YStack gap={16} padding={4}>
                <BodySmallRegular color="$gray700">
                    Import an Apex UTMA account created via Workstation into the
                    Fruitful database with rebalancer configuration.
                </BodySmallRegular>

                <YStack gap={4}>
                    <BodySmallEmphasized>Apex Account ID</BodySmallEmphasized>
                    <Input
                        placeholder="e.g. 01KM1Z9H4336DAKK4AZAE21EMG"
                        value={apexAccountId}
                        onChangeText={setApexAccountId}
                        size="tiny"
                        disabled={dryRunResult != null}
                    />
                </YStack>

                <XStack gap={12}>
                    <YStack gap={4} flex={1}>
                        <BodySmallEmphasized>Portfolio</BodySmallEmphasized>
                        <Dropdown
                            value={portfolio}
                            options={PORTFOLIO_OPTIONS}
                            onChange={(e) => {
                                setPortfolio(e.value as Portfolio);
                            }}
                            placeholder="Select portfolio"
                            style={{ width: '100%' }}
                            disabled={dryRunResult != null}
                        />
                    </YStack>
                    <YStack gap={4} flex={1}>
                        <BodySmallEmphasized>Risk Level</BodySmallEmphasized>
                        <Dropdown
                            value={riskLevel}
                            options={RISK_LEVEL_OPTIONS}
                            onChange={(e) => {
                                setRiskLevel(e.value as RiskLevel);
                            }}
                            placeholder="Select risk level"
                            style={{ width: '100%' }}
                            disabled={dryRunResult != null}
                        />
                    </YStack>
                </XStack>

                {errorMessage != null && (
                    <YStack
                        gap={4}
                        padding={12}
                        backgroundColor="$red200"
                        borderRadius={6}
                    >
                        <BodySmallEmphasized color="$red1100">
                            Error
                        </BodySmallEmphasized>
                        <BodySmallRegular color="$red1100">
                            {errorMessage}
                        </BodySmallRegular>
                    </YStack>
                )}

                {dryRunResult == null && (
                    <XStack gap={12} justifyContent="flex-end" marginTop={8}>
                        <Button
                            text="Cancel"
                            variant={ButtonVariant.SECONDARY}
                            size={ButtonSize.SMALL}
                            onPress={handleClose}
                        />
                        <Button
                            text={isPending ? 'Running...' : 'Dry Run'}
                            variant={ButtonVariant.PRIMARY}
                            size={ButtonSize.SMALL}
                            disabled={!canSubmit}
                            onPress={() => {
                                void handleDryRun();
                            }}
                        />
                    </XStack>
                )}

                {dryRunResult != null && (
                    <YStack gap={12}>
                        <YStack
                            gap={8}
                            padding={12}
                            backgroundColor="$gray100"
                            borderRadius={6}
                        >
                            <BodySmallEmphasized>
                                Apex Account Details
                            </BodySmallEmphasized>
                            <XStack gap={16} flexWrap="wrap">
                                <YStack gap={2}>
                                    <BodySmallRegular color="$gray700">
                                        Account #
                                    </BodySmallRegular>
                                    <BodySmallEmphasized>
                                        {
                                            dryRunResult.apexAccountDetails
                                                .accountNumber
                                        }
                                    </BodySmallEmphasized>
                                </YStack>
                                <YStack gap={2}>
                                    <BodySmallRegular color="$gray700">
                                        Type
                                    </BodySmallRegular>
                                    <BodySmallEmphasized>
                                        {
                                            dryRunResult.apexAccountDetails
                                                .registrationType
                                        }
                                    </BodySmallEmphasized>
                                </YStack>
                                <YStack gap={2}>
                                    <BodySmallRegular color="$gray700">
                                        State
                                    </BodySmallRegular>
                                    <Tag
                                        value={
                                            dryRunResult.apexAccountDetails
                                                .state
                                        }
                                        severity={
                                            dryRunResult.apexAccountDetails
                                                .state === 'OPEN'
                                                ? 'success'
                                                : 'warning'
                                        }
                                    />
                                </YStack>
                                <YStack gap={2}>
                                    <BodySmallRegular color="$gray700">
                                        Managed
                                    </BodySmallRegular>
                                    <Tag
                                        value={
                                            dryRunResult.apexAccountDetails
                                                .managed === true
                                                ? 'Yes'
                                                : 'No'
                                        }
                                        severity={
                                            dryRunResult.apexAccountDetails
                                                .managed === true
                                                ? 'success'
                                                : 'danger'
                                        }
                                    />
                                </YStack>
                            </XStack>
                            {dryRunResult.apexAccountDetails.managed !==
                                true && (
                                <BodySmallRegular color="$orange1000">
                                    Account is not marked as managed in Apex.
                                    Import will set managed=true automatically.
                                </BodySmallRegular>
                            )}
                            <YStack gap={4} marginTop={4}>
                                <BodySmallEmphasized>
                                    Enrollments
                                </BodySmallEmphasized>
                                {dryRunResult.apexAccountDetails.enrollments
                                    .length === 0 ? (
                                    <BodySmallRegular color="$orange1000">
                                        No enrollments found.
                                    </BodySmallRegular>
                                ) : (
                                    <XStack gap={6} flexWrap="wrap">
                                        {dryRunResult.apexAccountDetails.enrollments.map(
                                            (enrollment, idx) => (
                                                <Tag
                                                    key={idx}
                                                    value={`${enrollment.type} (${enrollment.state})`}
                                                    style={{ fontSize: 11 }}
                                                />
                                            ),
                                        )}
                                    </XStack>
                                )}
                                {!dryRunResult.apexAccountDetails.enrollments.some(
                                    (e) =>
                                        e.type.toUpperCase() ===
                                        'CASH_FDIC_CASH_SWEEP',
                                ) && (
                                    <BodySmallRegular color="$red1100">
                                        FDIC Sweep enrollment is missing. This
                                        should be set up in Workstation before
                                        importing.
                                    </BodySmallRegular>
                                )}
                            </YStack>
                        </YStack>

                        <YStack
                            gap={8}
                            padding={12}
                            backgroundColor={
                                dryRunResult.apexAccountDetails
                                    .trustedContact != null
                                    ? '$gray100'
                                    : '$orange300'
                            }
                            borderRadius={6}
                        >
                            <BodySmallEmphasized>
                                Trusted Contact
                            </BodySmallEmphasized>
                            {dryRunResult.apexAccountDetails.trustedContact !=
                            null ? (
                                <YStack gap={2}>
                                    <BodySmallRegular>
                                        {dryRunResult.apexAccountDetails
                                            .trustedContact.givenName ??
                                            '—'}{' '}
                                        {dryRunResult.apexAccountDetails
                                            .trustedContact.familyName ?? '—'}
                                    </BodySmallRegular>
                                    <BodySmallRegular color="$gray700">
                                        {dryRunResult.apexAccountDetails
                                            .trustedContact.emailAddress ??
                                            '—'}{' '}
                                        /{' '}
                                        {dryRunResult.apexAccountDetails
                                            .trustedContact.phoneNumber ?? '—'}
                                    </BodySmallRegular>
                                    <BodySmallRegular color="$orange1000">
                                        Existing TC will be preserved. IM ops
                                        will be alerted via Slack to verify it
                                        matches our records.
                                    </BodySmallRegular>
                                </YStack>
                            ) : (
                                <BodySmallRegular color="$gray700">
                                    No trusted contact on Apex account. Import
                                    will assign the user&apos;s Fruitful TC if
                                    one exists.
                                </BodySmallRegular>
                            )}
                        </YStack>

                        <YStack
                            gap={8}
                            padding={12}
                            backgroundColor="$gray100"
                            borderRadius={6}
                        >
                            <BodySmallEmphasized>
                                Matched User
                            </BodySmallEmphasized>
                            <BodySmallRegular>
                                {dryRunResult.matchedUser.firstName}{' '}
                                {dryRunResult.matchedUser.lastName} (
                                {dryRunResult.matchedUser.email})
                            </BodySmallRegular>
                            <BodySmallRegular color="$gray700">
                                User ID: {dryRunResult.matchedUser.userId}
                            </BodySmallRegular>
                        </YStack>

                        <XStack gap={8}>
                            <ValidationBadge
                                label="Legal Name"
                                passed={dryRunResult.validations.legalNameMatch}
                            />
                            <ValidationBadge
                                label="Address"
                                passed={dryRunResult.validations.addressMatch}
                            />
                        </XStack>

                        {dryRunResult.validations.legalNameDetails != null &&
                            !dryRunResult.validations.legalNameMatch && (
                                <YStack
                                    gap={4}
                                    padding={8}
                                    backgroundColor="$orange300"
                                    borderRadius={4}
                                >
                                    <BodySmallEmphasized color="$orange1100">
                                        Name Mismatch Details
                                    </BodySmallEmphasized>
                                    <BodySmallRegular>
                                        Apex:{' '}
                                        {dryRunResult.validations
                                            .legalNameDetails.apex.givenName ??
                                            '—'}{' '}
                                        {dryRunResult.validations
                                            .legalNameDetails.apex.familyName ??
                                            '—'}
                                    </BodySmallRegular>
                                    <BodySmallRegular>
                                        Fruitful:{' '}
                                        {dryRunResult.validations
                                            .legalNameDetails.fruitful
                                            .legalFirstName ?? '—'}{' '}
                                        {dryRunResult.validations
                                            .legalNameDetails.fruitful
                                            .legalLastName ?? '—'}
                                    </BodySmallRegular>
                                </YStack>
                            )}

                        <YStack gap={8}>
                            <BodySmallEmphasized>
                                {isExecuted
                                    ? 'Executed Actions'
                                    : 'Planned Actions'}
                            </BodySmallEmphasized>
                            {(
                                dryRunResult.executedResults ??
                                dryRunResult.plannedActions
                            ).map((action, idx) => (
                                <PlannedActionRow key={idx} action={action} />
                            ))}
                        </YStack>

                        <XStack
                            gap={12}
                            justifyContent="flex-end"
                            marginTop={8}
                        >
                            {isExecuted ? (
                                <Button
                                    text="Done"
                                    variant={ButtonVariant.PRIMARY}
                                    size={ButtonSize.SMALL}
                                    onPress={handleClose}
                                />
                            ) : (
                                <>
                                    <Button
                                        text="Back"
                                        variant={ButtonVariant.SECONDARY}
                                        size={ButtonSize.SMALL}
                                        onPress={() => {
                                            setDryRunResult(null);
                                        }}
                                    />
                                    <Button
                                        text={
                                            isPending
                                                ? 'Importing...'
                                                : 'Execute Import'
                                        }
                                        variant={ButtonVariant.PRIMARY}
                                        size={ButtonSize.SMALL}
                                        disabled={isPending}
                                        onPress={() => {
                                            void handleExecute();
                                        }}
                                    />
                                </>
                            )}
                        </XStack>
                    </YStack>
                )}
            </YStack>
        </Dialog>
    );
};
